import { useState, FC, useMemo, useEffect } from 'react';
import { ArrowBack } from '@mui/icons-material';
import { validateExistingMFA, getMe } from '../../fetch';
import { MFATextOption } from './mfa-text-option';
import { useSnackbar } from 'notistack';
import { IAuthUserMFAOptions, IUser, IDropdownResponse } from '../../models';
import { MFAMultipleExistingOptions } from './mfa-multiple-existing-options';
import { MFASetupOptions } from './mfa-setup-options';
import { Typography, Button, Box } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { userLogout } from '../../helpers';

interface IMFAFlow {
  existingMfaOptions: IAuthUserMFAOptions[];
  setUser: (user: IUser) => void;
  mfaTypes: IDropdownResponse[];
  resetLogin?: () => void;
}

export const MFAFlow: FC<IMFAFlow> = ({ existingMfaOptions, setUser, mfaTypes, resetLogin }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedInputCode, setSelectedInputCode] = useState<string>('');
  const [isLoadingMFA, setIsLoadingMFA] = useState<boolean>(false);
  const [isLoadingNewMFA, setIsLoadingNewMFA] = useState<boolean>(false);
  const [showSetupMfa, setShowSetupMfa] = useState<boolean>(false);
  const [selectedType, setSelectedType] = useState<IAuthUserMFAOptions | null>(null);
  const [showSetupButton, setShowSetupButton] = useState<boolean>(true);

  const history = useHistory();

  const handleLogin = async (
    e: any,
    option: IAuthUserMFAOptions,
    selectedCode: string,
    shouldSetUser: boolean = true
  ) => {
    e?.preventDefault();
    try {
      shouldSetUser ? setIsLoadingMFA(true) : setIsLoadingNewMFA(true);
      await validateExistingMFA({
        type: option.type,
        twoFactorAuthenticationId: option.twoFactorAuthenticationId,
        twoFactorCode: selectedCode
      });
      if (shouldSetUser) {
        const user = await getMe();
        setUser(user);
      } else {
        setShowSetupMfa(true);
      }
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error Logging in, please try again.', {
        variant: 'error'
      });
    } finally {
      shouldSetUser ? setIsLoadingMFA(false) : setIsLoadingNewMFA(false);
    }
  };
  const hasManyOptions = useMemo(
    () => existingMfaOptions && existingMfaOptions?.length > 1,
    [existingMfaOptions]
  );
  const hasNoExistingOptions = useMemo(
    () => existingMfaOptions && existingMfaOptions?.length === 0,
    [existingMfaOptions]
  );
  // user logged in has no mfa options setup and it is required
  useEffect(() => {
    if (hasNoExistingOptions) {
      setShowSetupMfa(true);
      setShowSetupButton(false);
    }
  }, [hasNoExistingOptions]);

  return (
    <>
      {existingMfaOptions && (
        <>
          {/* flow used for multiple existing options to choose from */}
          {!showSetupMfa && hasManyOptions && !hasNoExistingOptions && (
            <MFAMultipleExistingOptions
              existingMfaOptions={existingMfaOptions}
              isLoadingMFA={isLoadingMFA}
              handleLogin={handleLogin}
              handleSelectedType={val => setSelectedType(val)}
              setSelectedInputCode={setSelectedInputCode}
              resetLogin={resetLogin}
            />
          )}
          {/* flow used for one existing option to choose from */}
          {!showSetupMfa && !hasManyOptions && !hasNoExistingOptions && (
            <>
              <form onSubmit={e => handleLogin(e, existingMfaOptions?.[0], selectedInputCode)}>
                <MFATextOption
                  buttonText="Login"
                  isButtonDisabled={!selectedInputCode || isLoadingMFA}
                  isLoading={isLoadingMFA}
                  textFieldLabel="Code"
                  isTextOption={
                    existingMfaOptions?.filter(option => option.type === 'Text')?.length > 0
                  }
                  textFieldValue={selectedInputCode}
                  handleChange={(val: string) => {
                    setSelectedInputCode(val);
                  }}
                  authId={existingMfaOptions?.[0].twoFactorAuthenticationId}
                  alertText={
                    existingMfaOptions?.filter(option => option.type === 'Text')?.length > 0
                      ? 'Enter the code you should have received via SMS'
                      : `Enter the code in your authenticator ${
                          existingMfaOptions[0].type === 'Authy' ? 'Authy' : ''
                        } app.`
                  }
                  handleButtonClick={e =>
                    handleLogin(e, existingMfaOptions?.[0], selectedInputCode)
                  }
                />
              </form>
              <Box mt={1}>
                <Button
                  color="primary"
                  fullWidth={true}
                  variant="text"
                  startIcon={<ArrowBack fontSize="small" />}
                  onClick={() => {
                    userLogout();
                    resetLogin();
                  }}
                >
                  Back to Login
                </Button>
              </Box>
            </>
          )}
          {showSetupMfa && (
            <MFASetupOptions
              setUser={setUser}
              mfaTypes={mfaTypes}
              setShowSetupButton={setShowSetupButton}
            />
          )}
          {!showSetupMfa && (
            <Typography align="center" variant="caption" sx={{ marginTop: '1rem' }}>
              Don't have Two Factor Authentication set up? Select an option above, enter a valid
              code and then you can finish setting up a new authentication.
            </Typography>
          )}
          {showSetupButton && (
            <Box mt={1}>
              <Button
                fullWidth
                type="button"
                color="primary"
                size="small"
                startIcon={showSetupMfa ? <ArrowBack /> : undefined}
                disabled={!selectedInputCode || isLoadingNewMFA}
                onClick={e => {
                  if (showSetupMfa) {
                    history.push('/logout');
                  } else {
                    handleLogin(
                      e,
                      hasManyOptions ? selectedType : existingMfaOptions?.[0],
                      selectedInputCode,
                      false
                    );
                  }
                }}
              >
                {showSetupMfa ? 'Back to Login' : 'Setup Two Factor Authentication'}
              </Button>
            </Box>
          )}
        </>
      )}
    </>
  );
};
